<template>
  <div class="wwf-form light">
    <!-- HEAD -->
    <h3>Get in touch</h3>

    <p class="p-presentation">
      Complete the form to get in touch with Christine, or contact her directly on 02 8000 0303,
      <a href="mailto:giftsinwills@wwf.org.au">giftsinwills@wwf.org.au</a>.
    </p>

    <p class="p-presentation">
      <span class="details-statement">
        Your details will be treated with the strictest confidence and without
        any obligation
      </span>
    </p>

    <!-- INPUTS -->
    <input
      name="firstName"
      type="text"
      v-model="firstName"
      v-validate="{ required: true }"
      :class="{ 'border-red': errors.has('firstName') }"
      placeholder="First name"
      autocomplete="given-name"
    />

    <input
      name="lastName"
      type="text"
      v-model="lastName"
      v-validate="{ required: true }"
      :class="{ 'border-red': errors.has('lastName') }"
      placeholder="Last name"
      autocomplete="family-name"
    />

    <div class="small-12 medium-12 with-input-loading">
      <input
        name="email"
        type="email"
        v-model="email"
        v-validate="{ required: true, email: true, bequestCheckEmail: true }"
        :class="{
          'border-red': errors.has('email') || errors.has('bequestCheckEmail'),
        }"
        placeholder="Email"
        autocomplete="email"
      />
      <div class="input-loading" v-if="checkEmailLoading">
        <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4796/dist/assets/img/loading-tail-spin.svg" />
      </div>
      <span class="wwf-alert">
        <p v-if="errors.firstByRule('email', 'bequestCheckEmail')">
          {{ errors.first("email") }}
        </p>
      </span>
    </div>

    <div class="small-12 medium-12 with-input-loading">
      <input
        name="phone"
        type="tel"
        v-model="phone"
        v-validate="{ required: true, bequestCheckPhone: true }"
        :class="{
          'border-red': errors.has('phone') || errors.has('bequestCheckPhone'),
        }"
        placeholder="Phone"
        autocomplete="phone"
      />
      <div class="input-loading" v-if="phoneCheck.checkPhoneLoading">
        <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4796/dist/assets/img/loading-tail-spin.svg" />
      </div>
      <span class="wwf-alert">
        <p v-if="errors.firstByRule('phone', 'bequestCheckPhone')">
          {{ errors.first("phone") }}
        </p>
      </span>
    </div>

    <input
      name="postcode"
      type="text"
      v-model="postcode"
      v-validate="{ required: true, alpha_num: true }"
      :class="{ 'border-red': errors.has('postcode') }"
      placeholder="Postcode"
      autocomplete="postal-code"
    />

    <input
      name="address"
      type="text"
      v-model="address"
      placeholder="Postal address (optional)"
    />

    <select
      name="enquiryType"
      v-model="enquiryType"
      v-validate="{ required: true }"
      :class="{ 'border-red': errors.has('enquiryType') }"
    >
      <option value="" disabled>{{ enquiryLabel }}</option>
      <option v-for="(option, i) in enquiryOptions" :value="option" :key="i">
        {{ option }}
      </option>
    </select>

    <textarea
      name="comments"
      placeholder="Additional comments (optional)"
      v-model="comments"
    />

    <!-- DISCLAMER -->
    <div class="privacy-statement">
      <p>
        Any information shared is protected by our
        <a :href="privacyPolicyUrl" target="_blank"> Privacy Policy. </a>
      </p>
    </div>

    <!-- SUBMIT -->
    <input
      type="button"
      class="btn-submit"
      v-on:click="save()"
      :disabled="isSaving"
      :value="isSaving ? 'Loading...' : 'Submit'"
    />

    <!-- MANAGER -->
    <div class="text-center">
      <ul>
        <li id="round-thumb">
          <img
            style="border-radius: 50%; max-width: 65%"
            src="https://assets.wwf.org.au/image/upload/v1677198185/img-thumb-christine-robinson-200x200.jpg"
            alt="Christine Robinson profile photo © WWF-Aus"
          />
          <div>Your contact, Christine Robinson</div>
          <div>
            <p>02 8000 0303</p>
            <a href="mailto:giftsinwills@wwf.org.au">giftsinwills@wwf.org.au</a>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { Validator } from "vee-validate";
import { mapState } from "vuex";
import gtm from '../../utils/gtm';
import { getUtmParams } from '../../helpers';

export default {
  name: "bequest",
  data: function () {
    return {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      postcode: "",
      address: "",
      enquiryType: "",
      comments: "",
      isSaving: false,
      timeout: null,
      lastTimestamp: Date.now(),
      checkEmailLoading: false,
      emailRegex: /.+\@.+\..+/,
      phoneCheck: {
        timeout: null,
        lastTimestamp: Date.now(),
        checkPhoneLoading: false,
      },
    };
  },
  props: {
    enquiryLabel: {
      type: String,
      default: "Which of the following best describes you?",
    },
    enquiryOptions: {
      type: Array,
      default: () => [
        "I would like a free Will Guide",
        "I have already left a gift to WWF in my Will",
        "I am planning to leave a gift in my Will",
        "I am advising someone else about leaving a Gift in their Will",
      ],
    },
    thankYouUrl: {
      type: String,
      default: "https://wwf.org.au/donate/give-in-your-will/"
    },
    // Privacy policy
    privacyPolicyUrl: {
      type: String,
      default:"https://wwf.org.au/about-us/privacy-policy/"
    },
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
  },

  created() {
    // CHECK EMAIL
    const bequestCheckEmail = (email) => {
      if (email.length > 0 && this.emailRegex.test(email)) {
        return new Promise((resolve, reject) => {
          let timestamp = Date.now(); // timestamp for this api call
          this.lastTimestamp = timestamp; // 1 , 2 , 3
          this.checkEmailLoading = true;
          // Fetch Suggestions
          this.$store
            .dispatch("validateEmail", email)
            .then((response) => {
              if (response && timestamp >= this.lastTimestamp) {
                this.checkEmailLoading = false;
                resolve(response);
              }
            })
            .catch((err) => {
              console.error(err);
              this.checkEmailLoading = false;
              // If the call failed, do not keep the user from donating
              resolve({
                valid: true,
              });
            });
        });
      } else {
        return {
          valid: false,
          data: { message: "Email invalid" },
        };
      }
    };

    Validator.extend("bequestCheckEmail", {
      validate: bequestCheckEmail,
      getMessage: (field, params, data) => data.message,
    });

    // CHECK PHONE NUMBER
    const bequestCheckPhone = (phone) => {
      if (phone.length > 0) {
        return new Promise((resolve, reject) => {
          let timestamp = Date.now(); // timestamp for this api call
          this.phoneCheck.lastTimestamp = timestamp; // 1 , 2 , 3
          this.phoneCheck.checkPhoneLoading = true;
          // Fetch Suggestions
          this.$store
            .dispatch("validatePhone", phone)
            .then((response) => {
              if (response && timestamp >= this.phoneCheck.lastTimestamp) {
                this.phoneCheck.checkPhoneLoading = false;
                resolve(response);
              }
            })
            .catch((err) => {
              console.error(err);
              this.phoneCheck.checkPhoneLoading = false;
              // If the call failed, do not keep the user from donating
              resolve({
                valid: true,
              });
            });
        });
      } else {
        return {
          valid: false,
          data: { message: "Invalid phone number, try again" },
        };
      }
    };

    Validator.extend("bequestCheckPhone", {
      validate: bequestCheckPhone,
      getMessage: (field, params, data) => data.message,
    });
  },

  methods: {
    save: function () {
      // Exit the function if it's busy
      if (this.isSaving) {
        return;
      }

      this.$validator.validateAll().then(async (result) => {
        try {
          if (!result) {
            return;
          }
          this.isSaving = true;

          // Generate a token for a special action
          await this.$store.dispatch("generateCaptchaToken", "bequest");

          const response = await Vue.http.post(
            wwfconfigs.WEB_API + "/bequest",
            {
              firstName: this.firstName,
              lastName: this.lastName,
              email: this.email,
              postcode: this.postcode,
              phone: this.phone,
              address: this.address,
              enquiryType: this.enquiryType,
              comments: this.comments,
              source: "website-bequest",
              ssoId: this.$store.getters.ssoId,
              sourceUrl: window.top.location.href.split('?')[0] + getUtmParams(window.location.href),
            }
          );

          this.isSaving = false;
          // Wait for datalayer before redirecting 
          const callback = () => {
            setTimeout(() => {
              // Redirect to thank you page
              window.top.location.href = this.thankYouUrl;
            }, 1000);
          };
          // Push to datalayer
          gtm.formSubmitSuccessEvent('Bequest', callback);
          
        } catch (err) {
          console.error(err);
        }

        // Reset form
        this.firstName = "";
        this.lastName = "";
        this.email = "";
        this.postcode = "";
        this.phone = "";
        this.address = "";
        this.enquiryType = "";
        this.comments = "";

        this.isSaving = false;
      });
    },
  },
};
</script>

<style scoped>
p.p-presentation {
  margin-bottom: 24px;
}

.btn-submit {
  width: 100%;
  margin-bottom: 24px;
}

.input-loading {
  top: 6px;
}

.details-statement {
  font-style: italic;
}

/* From elcom, integrated for Makers-tech */
h1, h2, h3 {
  font-family: "WWF", sans-serif;
  position: relative;
  z-index: 1;
}

h3:before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 30px;
  height: 7px;
  background-color: #F37043;
}

a {
  color: #f37043;
}

ul, ul, p, a {
  font-family: 'Lato', sans-serif;
}

h4, h5, h6, p, a {
  font-family: 'Lato', sans-serif;
  position: relative;
  z-index: 1;
}

body *, html * {
  outline: 0;
}
/* END */

/* Override Makers Tech's body style */
.wwf-form {
  background-color: white;
  padding: 20px;
}
/* END */
</style>


// WEBPACK FOOTER //
// Bequest.vue?440522e0